import { RefObject, useCallback, useEffect, useState } from 'react'
import {
  useFieldArray,
  UseFieldArrayReturn,
  UseFormReturn,
  useWatch,
} from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import useWebSocket from 'react-use-websocket'
import {
  AdminPartisanChartFormValues,
  useAdminPartisanChartForm,
} from '../../../../forms/charts/admin/admin-chart-partisan-form'
import { ChartTypeEnum } from '../../../../models/chart'
import { State } from '../../../../models/state'
import { ChartsService } from '../../../../services/charts-service'
import { CognitoService } from '../../../../services/cognito-service'
import { PartiesService } from '../../../../services/parties-service'
import { StateService } from '../../../../services/state-services'

export interface AdminManagePartisanChartState {
  mode: string
  loading: boolean
  form: UseFormReturn<AdminPartisanChartFormValues>
  partiesFieldArray: UseFieldArrayReturn<
    AdminPartisanChartFormValues,
    'parties'
  >
  racesFieldArray: UseFieldArrayReturn<AdminPartisanChartFormValues, 'races'>
  raceTypes: State[]
  saveAndContinueDialog: boolean
  states: State[]
  showFileUpload: boolean
  chartId: string
  saving: boolean

  useExistingBallotFile: () => void
  uploadNewBallotFile: () => void
  showAlreadyBallotFileDialog: boolean
  openSaveAndContinueDialog: () => void
  closeSaveAndContinueDialog: () => void
  addAllPartisanRaces(
    name: string,
    dialogRef: RefObject<HTMLDialogElement>
  ): void
  addNewRace: () => void
  removeRace: (index: number) => void
  changeRaceType: (
    name: string,
    raceType: string,
    type: string,
    index: number
  ) => void
  moveRace: (dragIndex: number, hoverIndex: number) => void
  sortRaces: () => void
  moveParty: (dragIndex: number, hoverIndex: number) => void
  cancel: () => void
  saveAndExit: () => void
  saveAndContinue: (action: 'full-deck' | 'chart') => void
  cancelFileUpload: () => void
  addPoliticalParty: (partyName: string, partyAbbreviation: string) => void
}

const useAdminManagePartisanChart = (
  chartType: ChartTypeEnum
): AdminManagePartisanChartState => {
  const [loading, setLoading] = useState<boolean>(true)
  const [mode, setMode] = useState<string>('create')
  const [saving, setSaving] = useState<boolean>(false)
  const [chartId, setChartId] = useState<string>('')
  const [raceTypes, setRaceTypes] = useState<State[]>([])
  const [states, setStates] = useState<State[]>([])
  const [shouldWebSocketConnect, setShouldWebSocketConnect] = useState(false)
  const [saveAndContinueDialog, setSaveAndContinueDialog] =
    useState<boolean>(false)
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false)
  const [chartHasBallotFile, setChartHasBallotFile] = useState(false)
  const [showAlreadyBallotFileDialog, setShowAlreadyBallotFileDialog] =
    useState(false)
  const [token, setToken] = useState<string>('')
  const [dialogAction, setDialogAction] = useState<'full-deck' | 'chart'>(
    'chart'
  )
  const [gotFirstWebSocketMessage, setGotFirstWebSocketMessage] =
    useState<boolean>(false)

  const params = useParams<{ chartId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const form = useAdminPartisanChartForm()

  const partiesFieldArray = useFieldArray({
    control: form.control,
    name: 'parties',
  })

  const formParties = useWatch({ control: form.control, name: 'parties' })

  const racesFieldArray = useFieldArray({
    control: form.control,
    name: 'races',
  })

  useEffect(() => {
    ; (async () => {
      const _raceTypes = await ChartsService.getRaceTypes()
      const parties = await PartiesService.getParties()
      setRaceTypes(_raceTypes)
      const _states = await StateService.getActiveStatesByElectionType(
        chartType
      )
      setStates(_states)

      if (params.chartId) {
        setMode('edit')
        const isEditing = !location.pathname.includes('duplicate')
        setChartId(params.chartId)
        const chart = await ChartsService.getChart(params.chartId)
        const selectedPoliticalParties = [
          ...chart.politicalParties.map((party, index) => ({
            id: index,
            name: party.name,
            abbv: party.abbv,
            selected: true,
          })),
        ]
        const allOtherParties = [
          ...parties
            .filter(
              (party) =>
                !selectedPoliticalParties.find((_) => _.name === party.name)
            )
            .map((party) => ({
              id: party.id,
              name: party.name,
              abbv: party.abbv,
              selected:
                chart.politicalParties.findIndex((_) => _.name === party.name) >
                -1,
            })),
        ]
        if (isEditing) setChartHasBallotFile(Boolean(chart.ballotFile))
        form.reset({
          municipality: chart.municipality,
          county: chart.county,
          precinct: isEditing ? chart.precinct : '',
          electionDate: getDateCleanedForLoading(chart.electionDate),
          showCandidateNames: chart.showCandidateNames,
          createPreferredParty: chart.createStraightParty,
          state: _states
            .find((s) => s.name === chart.state.name)
            ?.id.toString(),
          parties: [...selectedPoliticalParties, ...allOtherParties],
          races: chart.races.map((race) => ({
            id: race.id,
            name: race.name,
            type: race.type,
            raceType:
              race.raceType.name !== 'Partisan'
                ? race.raceType.name
                : race.politicalParties[0].name, // if it's partisan, we need to set the raceType to the party name
            candidates: race.candidates,
            candidateNames: race.candidateNames.map((item) => ({
              name: item.name,
              order: +item.order,
            })),
            voteFor: race.voteFor,
            writeIns: race.writeIns,
            parties: race.politicalParties.map((party) => ({
              abbv: party.abbv,
              candidateNames: party.candidateNames.map((item) => ({
                id: item.id,
                name: item.name,
                order: +item.order,
              })),
              candidates: party.candidates,
              id: party.id,
              name: party.name,
            })),
          })),
        })
      } else {
        setMode('create')
        form.reset({
          parties: parties.map((party) => ({
            id: party.id,
            name: party.name,
            abbv: party.abbv,
            selected: false,
          })),
        })
      }

      setLoading(false)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getWebsocketUrl = useCallback(() => {
    return `${process.env.REACT_APP_SERVICE_URL?.replace(
      'http://',
      'ws://'
    ).replace('https://', 'wss://')}/charts/admin/process/${chartId}`
  }, [chartId])

  useWebSocket(
    getWebsocketUrl,
    {
      share: false,
      shouldReconnect: (closeEvent) => false,
      onClose: (event: WebSocketEventMap['close']) => {
        setSaving(false)
        setToken('')
        return
      },
      onMessage: (event: WebSocketEventMap['message']) => {
        if (!gotFirstWebSocketMessage) {
          toast.remove()
          toast.success('Generating Chart Files')
          setGotFirstWebSocketMessage(true)
        }
        console.log('message', event.data)
        if (event.data.includes('FINISHED')) {
          toast.remove()
          // set should connect to false
          setShouldWebSocketConnect(false)
          setToken('')
          setSaving(false)
          if (dialogAction === 'full-deck') {
            if (chartHasBallotFile) setShowAlreadyBallotFileDialog(true)
            else setShowFileUpload(true)
          } else {
            navigate(-1)
          }
        } else if (event.data.includes('ERROR')) {
          // set should connect to false
          setShouldWebSocketConnect(false)
          toast.error('Error occurred while generating the chart files.')
          setToken('')
          setSaving(false)
        }
      },
      onError: (event: WebSocketEventMap['error']) => {
        setShouldWebSocketConnect(false)
        toast.error('Error occurred while generating the chart files.')
        setToken('')
        setSaving(false)
      },
      protocols: ['Authorization', token],
    },
    shouldWebSocketConnect
  )

  const onSubmit = async (
    values: AdminPartisanChartFormValues,
    generateChart: boolean
  ): Promise<string> => {
    const selectedParties = values.parties.filter((_) => _.selected)
    const isEditing = !location.pathname.includes('duplicate') && chartId

    const payload = {
      ...(isEditing ? { id: chartId } : {}),
      adminchart: true,
      county: values.county.includes('/')
        ? values.county.replace('/', '-')
        : values.county.includes('\\')
          ? values.county.replace('\\', '-')
          : values.county,
      electionTypeId: chartType,
      electiondate: `${values.electionDate}T00:00:00Z`,
      labelsonright: false,
      municipality: values.municipality.includes('/')
        ? values.municipality.replace('/', '-')
        : values.municipality.includes('\\')
          ? values.municipality.replace('\\', '-')
          : values.municipality,
      precinct: values.precinct.includes('/')
        ? values.precinct.replace('/', '-')
        : values.precinct.includes('\\')
          ? values.precinct.replace('\\', '-')
          : values.precinct,
      stateId: parseInt(values.state),
      showCandidateNames: values.showCandidateNames,
      createStraightParty: values.createPreferredParty,

      politicalParties: selectedParties.map((party, idx) => ({
        ...(isEditing ? { id: party!.id as number } : {}),
        name: party.name,
        abbv: party.abbv,
        order: idx,
      })),

      races: values.races.map((race, raceIdx) => {
        return {
          ...(isEditing ? { id: race!.id as string } : {}),
          candidates:
            race.type.toLowerCase() === 'proposal'
              ? 0
              : race.candidates < 0
                ? 0
                : race.candidates,
          candidateNames: race.candidateNames
            ? values.showCandidateNames
              ? race.candidateNames
                .map((item) => ({ ...item, order: +item.order }))
                .filter(Boolean)
              : []
            : [],
          marks: [],
          name: race.name,
          order: raceIdx,
          type: race.type,
          raceTypeId:
            race.type.toLowerCase() === 'proposal'
              ? 1
              : race.raceType.toLowerCase() === 'non-partisan'
                ? 1
                : 0,
          voteFor: race.type.toLowerCase() === 'proposal' ? 1 : race.voteFor,
          writeIns: race.type.toLowerCase() === 'proposal' ? 0 : race.writeIns,

          politicalParties:
            race.type.toLowerCase() === 'proposal' ||
              race.type.toLowerCase() === 'non-partisan'
              ? []
              : selectedParties.filter((p) => p.name === race.raceType).length >
                0
                ? race.parties.map((party, idx) => ({
                  ...(isEditing ? { id: race?.parties[idx]?.id } : {}),
                  abbv: party.abbv,
                  candidates: race?.parties[idx]?.candidates || 0,
                  candidateNames: party.candidateNames
                    ? values.showCandidateNames
                      ? party.candidateNames
                        .map((item) => ({ ...item, order: +item.order }))
                        .filter(Boolean)
                      : []
                    : [],
                  name: party.name,
                  order: idx,
                }))
                : [],

          raceType: race.raceType === 'Non-Partisan' ? 1 : 0,
        }
      }),
    }

    let returnedId = ''
    if (isEditing) {
      // @ts-ignore
      const val = await ChartsService.adminEditChart(payload)
      returnedId = val.id
    } else {
      // @ts-ignore
      const val = await ChartsService.adminCreateChart(payload)
      returnedId = val.id
    }

    if (generateChart && returnedId !== '') {
      // this process may take a bit, so we have to websockets to make sure we stay connected
      const session = await CognitoService.getSession()
      if (session) {
        setToken(session.getIdToken().getJwtToken())
      }
      setChartId(returnedId)
      setShouldWebSocketConnect(true)
    }

    return returnedId
  }

  const addAllPartisanRaces = (
    name: string,
    dialogRef: RefObject<HTMLDialogElement>
  ) => {
    formParties
      .filter((p) => p.selected === true)
      .forEach((p) => {
        // add a new race for this party
        racesFieldArray.append({
          name: name,
          voteFor: 1,
          writeIns: 1,
          candidates: 0,
          candidateNames: [],
          parties: [
            {
              ...p,
              candidates: 0,
              candidateNames: [],
              id: p.id ? p.id.toString() : undefined,
            },
          ],
          type: 'Race',
          raceType: p.name,
        })
      })

    dialogRef.current?.close()
  }

  const addNewRace = () => {
    form.clearErrors('races')
    const lastRace = form.getValues().races
      ? form.getValues().races[
      form.getValues().races ? form.getValues().races.length - 1 : 0
      ]
      : undefined
    racesFieldArray.append({
      name: '',
      voteFor: 1,
      writeIns: 1,
      candidates: 0,
      candidateNames: [],
      parties: [
        {
          ...formParties[0],
          candidateNames: [],
          candidates: 0,
          id: formParties[0].id ? formParties[0].id.toString() : undefined,
        },
      ],
      type: lastRace ? lastRace.type : 'Race',
      raceType: lastRace ? lastRace.raceType : 'Non-Partisan',
    })
  }

  const removeRace = (index: number) => racesFieldArray.remove(index)

  const moveRace = (dragIndex: number, hoverIndex: number) => {
    const { races } = form.getValues()
    const dragCard = races[dragIndex]
    races.splice(dragIndex, 1)
    races.splice(hoverIndex, 0, dragCard)
    form.setValue('races', [...races])
  }

  const changeRaceType = (
    name: string,
    raceType: string,
    type: string,
    index: number
  ) => {
    const party = formParties.find((item) => item.name === raceType)
    racesFieldArray.update(index, {
      name: name,
      voteFor: 1,
      writeIns: 1,
      candidates: 0,
      candidateNames: [],
      parties: party
        ? [
          {
            ...party,
            candidateNames: [],
            candidates: 0,
            id: party.id ? party.id.toString() : undefined,
          },
        ]
        : [],
      type: type,
      raceType: raceType,
    })
  }

  const moveParty = (dragIndex: number, hoverIndex: number) => {
    const { parties } = form.getValues()
    const dragCard = parties[dragIndex]
    parties.splice(dragIndex, 1)
    parties.splice(hoverIndex, 0, dragCard)
    form.setValue('parties', [...parties])
  }

  const sortRaces = () => {
    const { races, parties } = form.getValues()

    const order: { [key: string]: number } = parties.reduce(
      (acc, party, i) => ({ ...acc, [party.name]: i }),
      { 'Non-Partisan': 25 }
    )

    const sortedRaces = races
      .filter((race) => race.type === 'Race')
      .sort((a, b) => {
        if (order[a.raceType] > order[b.raceType]) {
          return 1
        } else if (order[a.raceType] < order[b.raceType]) {
          return -1
        } else {
          return 0
        }
      })

    const proposals = races.filter((race) => race.type === 'Proposal')

    form.setValue('races', [...sortedRaces, ...proposals])
  }

  const cancel = () => {
    navigate(-1)
  }

  const saveAndExit = async () => {
    await toast.promise(
      form.handleSubmit(
        (values) => onSubmit(values, false),
        () => {
          // eslint-disable-next-line no-throw-literal
          throw 'Form is not valid'
        }
      )(),
      {
        loading: 'Saving...',
        success: () => {
          navigate(-1)
          return <b>Successful chart save!</b>
        },
        error: (e) => {
          return e?.response?.data
            ? e.response.data
            : e.message ?? e ?? 'An Error Occurred.'
        },
      },
      { duration: 5000 }
    )
  }

  const saveAndContinue = async (action: 'full-deck' | 'chart') => {
    try {
      setSaving(true)
      setDialogAction(action)
      await toast.promise(
        form.handleSubmit(
          (values) => {
            onSubmit(values, true).catch((e) => {
              toast.error(
                e?.response?.data
                  ? e.response.data
                  : e.message ?? 'An Error Occurred.'
              )
            })
          },
          () => {
            toast.error('Form is not valid')
          }
        )(),
        {
          loading: 'Saving...',
          success: () => {
            setSaveAndContinueDialog(false)
            return <b>Successful chart save!</b>
          },
          error: (e: any) => {
            return e?.response?.data
              ? e.response.data
              : e.message ?? e ?? 'An Error Occurred.'
          },
        },
        { duration: 5000 }
      )
    } catch (e) {
      console.log(e)
      setSaving(false)
    }
  }

  const addPoliticalParty = (partyName: string, partyAbbreviation: string) => {
    partiesFieldArray.append({
      name: partyName,
      abbv: partyAbbreviation,
      selected: true,
    })
  }

  const getDateCleanedForLoading = (date: string): string => {
    const d = new Date(date)
    const temp = `${d.getUTCFullYear()}-${d.getUTCMonth() + 1 < 10
      ? '0' + (d.getUTCMonth() + 1)
      : d.getUTCMonth() + 1
      }-${d.getUTCDate() < 10 ? '0' + d.getUTCDate() : d.getUTCDate()}`
    return temp
  }

  const cancelFileUpload = () => {
    setShowFileUpload(false)
    navigate('/elections/partisan?status=Completed')
  }

  const useExistingBallotFile = () => {
    navigate(`/mark-ballot/${chartId}`)
  }

  const uploadNewBallotFile = () => {
    setShowAlreadyBallotFileDialog(false)
    setShowFileUpload(true)
  }

  return {
    mode,
    loading,
    form,
    raceTypes,
    partiesFieldArray,
    racesFieldArray,
    saveAndContinueDialog,
    states,
    chartId,
    saving,
    showFileUpload,
    showAlreadyBallotFileDialog,

    closeSaveAndContinueDialog: () => setSaveAndContinueDialog(false),
    openSaveAndContinueDialog: () => setSaveAndContinueDialog(true),
    addAllPartisanRaces,
    addNewRace,
    removeRace,
    moveRace,
    sortRaces,
    moveParty,
    cancel,
    saveAndExit,
    saveAndContinue,
    cancelFileUpload,
    useExistingBallotFile,
    uploadNewBallotFile,
    addPoliticalParty,
    changeRaceType,
  }
}

export default useAdminManagePartisanChart
